"use client";

import { useGlobalStore } from "@/mono/store/global";
import {
  createElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useCallback, useMemo } from "react";
import { shuffle } from "txt-shuffle";
import useIsomorphicLayoutEffect from "../lib/hooks/useIsomorphicLayoutEffect";
import { useScrollRigScrollTrigger } from "../lib/hooks/useScrollRigScrollTrigger";
import { randomSentences } from "common/data";

const fps = 15;

const getRandomSentence = () => {
  if (!Array.isArray(randomSentences) || randomSentences.length === 0)
    return "";
  const index = Math.floor(Math.random() * randomSentences.length);
  return randomSentences[index];
};

// DOC: https://github.com/brunoimbrizi/txt-shuffle
const ShuffledText = forwardRef(
  (
    {
      tag = "span",
      className = "",
      wrapperClassName = "",
      text: initialText,
      immediate = false,
      shuffleOptions = {},
      hoverAnimation = true,
      removeOnOut = false,
      animateOnScroll = false,
      noMeasure = false,

      // 9CA only
      useRandomText = false,

      ...props
    },
    ref
  ) => {
    // const text = useRef(initialText);
    const [text, setText] = useState(initialText);

    useEffect(() => {
      if (useRandomText) {
        setText(getRandomSentence());
      }
    }, [useRandomText]);

    const [textRef, containerRef] = [useRef(), useRef()];
    const windowSize = useGlobalStore((s) => s.windowSize);
    const measurementRef = useRef(null);

    const shuffleText = useCallback(
      (_text, _options) => {
        const options = _options ?? shuffleOptions;

        return shuffle({
          text: _text,
          fps: fps,
          direction: "random",
          ...options,
          onUpdate: (output) => {
            if (textRef.current) {
              textRef.current.textContent = output;
            }
          },
          onComplete: () => {
            _options?.onComplete?.();
          },
        });
      },
      [shuffleOptions]
    );

    // Set initial dimensions with a persistent hidden measurement element
    useIsomorphicLayoutEffect(() => {
      if (noMeasure || !containerRef.current) return;

      if (!measurementRef.current) {
        measurementRef.current = document.createElement("div");
        measurementRef.current.style.visibility = "hidden";
        measurementRef.current.style.position = "absolute";
        measurementRef.current.style.pointerEvents = "none";
        measurementRef.current.style.top = "-9999px";
        measurementRef.current.style.left = "-9999px";
        document.body.appendChild(measurementRef.current);
      }

      const measureEl = measurementRef.current;
      measureEl.style.width = `${containerRef.current.offsetWidth}px`;
      measureEl.className = wrapperClassName;
      measureEl.innerHTML = "";

      const innerText = document.createElement(tag);
      innerText.className = `txt-shuffle ${className}`;
      innerText.textContent = text;
      measureEl.appendChild(innerText);

      const height = measureEl.offsetHeight;
      if (height > 0) {
        containerRef.current.style.height = `${height}px`;
      }
    }, [text, tag, className, wrapperClassName, windowSize, noMeasure]);

    // Cleanup measurement element on unmount
    useEffect(() => {
      return () => {
        if (measurementRef.current && measurementRef.current.parentNode) {
          document.body.removeChild(measurementRef.current);
          measurementRef.current = null;
        }
      };
    }, []);

    const animateIn = useCallback(
      (props) => {
        shuffleText(text, { ...props, animation: "show", fps: fps });
      },
      [text, shuffleText]
    );

    const animateOut = useCallback(
      (props) => {
        shuffleText(text, {
          ...props,
          animation: "hide",
          fps: fps,
          onComplete: () => {
            if (removeOnOut && containerRef.current) {
              containerRef.current.style.display = "none";
            }
            props?.onComplete?.();
          },
        });
      },
      [text, shuffleText, removeOnOut]
    );

    const handleScrollIn = useCallback(() => {
      if (!animateOnScroll) return;
      animateIn();
    }, [animateOnScroll, animateIn]);

    useScrollRigScrollTrigger({
      el: containerRef,
      handleIn: handleScrollIn,
    });

    useEffect(() => {
      if (immediate) {
        shuffleText(text);
      }
    }, [text, immediate, shuffleText]);

    const getTextWidth = useCallback((newText) => {
      return new Promise((resolve) => {
        if (!textRef.current) {
          resolve(0);
          return;
        }

        requestAnimationFrame(() => {
          const originalText = textRef.current.textContent;
          textRef.current.textContent = newText;
          const width = textRef.current.offsetWidth;
          textRef.current.textContent = originalText;
          resolve(width);
        });
      });
    }, []);

    const shuffleInPlace = useCallback(() => {
      shuffleText(text, { ...props, animation: "stay", fps: fps });
    }, [text, shuffleText, props]);

    const setTextHandler = useCallback(
      (newText) => {
        setText(newText);
        return getTextWidth(newText);
      },
      [getTextWidth]
    );

    const imperativeValue = useMemo(
      () => ({
        animateIn,
        animateOut,
        shuffleInPlace,
        setText: setTextHandler,
        element: textRef.current,
        container: containerRef.current,
        getTextWidth,
      }),
      [animateIn, animateOut, shuffleInPlace, setTextHandler, getTextWidth]
    );

    useImperativeHandle(ref, () => imperativeValue, [imperativeValue]);

    return (
      <div
        className={`txt-shuffle-wrapper ${wrapperClassName}`}
        ref={containerRef}
        onMouseEnter={useCallback(() => {
          if (!hoverAnimation) return;
          shuffleText(text, {
            duration: 0.4,
            animation: "stay",
            direction: "random",
            fps: fps,
          });
        }, [hoverAnimation, text, shuffleText])}
        {...props}
      >
        {createElement(
          tag,
          {
            ref: textRef,
            className: `txt-shuffle ${className}`,
          },
          text
        )}
        {props.children}
      </div>
    );
  }
);

ShuffledText.displayName = "ShuffledText";

export default ShuffledText;
